import { motion } from "framer-motion";
import { BookMarked, Users, Heart, Check } from "lucide-react";
import { Reveal } from "./Reveal";

const tiers = [
  {
    icon: BookMarked,
    name: "Little Reader",
    price: "₹500",
    period: "/month",
    features: ["One child, unlimited visits", "Borrow 3 books at a time", "Storytelling circles included"],
    featured: false,
    id: "little-reader",
  },
  {
    icon: Users,
    name: "Family Shelf",
    price: "₹900",
    period: "/month",
    features: ["Up to three children", "Borrow 8 books at a time", "All studios & weekend workshops", "Priority seats at events"],
    featured: true,
    id: "family-shelf",
  },
  {
    icon: Heart,
    name: "Patron Circle",
    price: "₹2,500",
    period: "/month",
    features: ["Everything in Family Shelf", "Sponsor a shelf for the community", "Educator borrowing privileges", "Founders' annual reading evening"],
    featured: false,
    id: "patron-circle",
  },
];

export const Membership = () => (
  <section
    id="membership"
    data-testid="membership-section"
    className="border-y border-ink/10 bg-sand py-28 md:py-40"
  >
    <div className="mx-auto max-w-[1400px] px-6 md:px-12">
      <Reveal>
        <p className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.25em] text-plum">
          05 · Membership
        </p>
        <h2 className="mx-auto max-w-3xl text-center font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl md:text-6xl">
          Belong to a place that <em className="text-plum">belongs to children.</em>
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-center text-base leading-relaxed text-slate2">
          Membership keeps the lights warm and the shelves full. Every plan
          includes all six studios — nothing here is locked behind a paywall.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
        {tiers.map((t, i) => (
          <Reveal key={t.id} delay={0.08 * i}>
            <motion.div
              data-testid={`membership-tier-${t.id}`}
              whileHover={{ y: -8 }}
              transition={{ type: "spring", stiffness: 240, damping: 20 }}
              className={`relative flex h-full flex-col rounded-3xl border p-8 md:p-10 ${
                t.featured
                  ? "border-plum bg-plum text-cream shadow-[0_40px_80px_-40px_rgba(93,46,70,0.6)]"
                  : "border-ink/10 bg-cream text-ink"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-8 rounded-full bg-terracotta px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-cream">
                  Most loved
                </span>
              )}
              <t.icon size={26} strokeWidth={1.6} className={t.featured ? "text-terracotta" : "text-plum"} />
              <h3 className="mt-6 font-serif text-3xl">{t.name}</h3>
              <p className="mt-4">
                <span className="font-serif text-5xl tracking-tight">{t.price}</span>
                <span className={`text-sm ${t.featured ? "text-cream/70" : "text-slate2"}`}>{t.period}</span>
              </p>
              <ul className="mt-8 flex-1 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
                    <Check size={16} className={`mt-0.5 shrink-0 ${t.featured ? "text-sage" : "text-plum"}`} />
                    <span className={t.featured ? "text-cream/85" : "text-slate2"}>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#visit"
                data-testid={`membership-cta-${t.id}`}
                className={`mt-10 rounded-full px-6 py-3.5 text-center text-sm font-semibold transition-transform duration-300 hover:scale-105 ${
                  t.featured
                    ? "bg-cream text-plum"
                    : "border border-ink/25 text-ink hover:border-plum hover:text-plum"
                }`}
              >
                Become a Library Member
              </a>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
